// The header's "Team drafts" link, with how many open drafts the team has. Shown to
// support leads and above only, which is presentation: GET /api/ticket-drafts/team refuses
// everyone else (docs/API.md §12.6).
//
// The count is read when the header mounts, when the browser tab regains focus, and when
// the Team drafts page, having read the list itself, hands over the count
// (TEAM_DRAFTS_CHANGED). There is no timer. A lead with nothing open still sees the link,
// with no number beside it.

import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router';

import { api } from '../../api/client.js';
import { useSession } from '../../session/SessionProvider.jsx';
import { atLeastRole } from '../../session/roles.js';
import { teamLinkName } from './team-drafts.js';
import { supportPath } from '../../support-path.js';

/** Dispatched on `window` by the Team drafts page after it reads the list: `detail.count` is how many are open. */
export const TEAM_DRAFTS_CHANGED = 'team-drafts-changed';

export default function TeamDraftsLink() {
  const user = useSession();
  const isLead = atLeastRole(user, 'support', 'lead');
  const [count, setCount] = useState(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!isLead) return undefined;
    let active = true;

    function read() {
      if (inFlight.current) return;
      inFlight.current = true;
      api.get('/ticket-drafts/team')
        .then((response) => {
          if (active && Array.isArray(response?.data)) setCount(response.data.length);
        })
        // The link stays, without a number: the page itself reports a failed read.
        .catch(() => {})
        .finally(() => { inFlight.current = false; });
    }

    function onCount(event) {
      const count = event.detail?.count;
      if (Number.isInteger(count)) setCount(count);
    }

    read();
    window.addEventListener('focus', read);
    window.addEventListener(TEAM_DRAFTS_CHANGED, onCount);
    return () => {
      active = false;
      window.removeEventListener('focus', read);
      window.removeEventListener(TEAM_DRAFTS_CHANGED, onCount);
    };
  }, [isLead]);

  if (!isLead) return null;

  return (
    <NavLink
      to={supportPath('/tickets/drafts/team')}
      aria-label={teamLinkName(count)}
      className={({ isActive }) =>
        `flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-sunken text-ink'
            : 'text-ink-secondary hover:text-ink hover:bg-sunken'
        }`
      }
    >
      Team drafts
      {count > 0 && (
        <span
          aria-hidden="true"
          className="rounded-full bg-sunken px-1.5 text-xs tabular-nums text-ink-secondary ring-1 ring-inset ring-line-strong"
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}
